import React, { useEffect, useState } from 'react';
import _ from 'lodash'
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Edit2 } from 'lucide-react';
import { Switch } from '@headlessui/react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAutomation } from '@/api/hooks/useAutomation';
import { AutomationDetailMain, AutomationDetailSidebar, ViewAutomationHeader } from '@/components/autopilot/detail-autopilot';
import { blueprints } from './create';
import { useDevices } from '@/api/hooks/useDevices';
import { editAutomation } from '@/api/automation';
import { App } from 'antd';
import { SUCCESS_MESSAGE } from '@/utils/constants';
import { useBlueprints } from '@/api/hooks/useBlueprint';

interface AutomationDetailProps {
  id?: string;
  isEditMode: boolean;
}

export function AutomationDetail({ id, isEditMode }: AutomationDetailProps) {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const params = useParams();
  const automationId = id || params.id;
  const [states, setStates] = useState<State[]>([]);
  const [activeTab, setActiveTab] = useState<'states' | 'constraints'>('states');
  const [constraints, setConstraints] = useState<Constraint[]>([]);
  const [parameters, setParameters] = useState([])
  const [automation, setAutomation] = useState();
  const [availableDevices, setAvailableDevices] = useState([]);
  const [isEdit, setIsEdit] = useState(isEditMode)
  const [interval, setInterval] = useState([0, 0]);
  const [automationName, setAutomationName] = useState('');
  const disabled = !isEdit

  const { data: automationData, isLoading: automationLoading, refetch } = useAutomation(automationId);
  const { data: systemData, isLoading, error } = useDevices({ expand: 'devices' });
  const { data: blueprintsData, isLoading: blueprintsLoading } = useBlueprints();

  const onEdit = async () => {
    if (!isEdit) {
      setIsEdit(true)
    }
    else {
      try {
        await editAutomation(automationId, {
          name: automationName,
          config: {
            ...(automation?.blueprint?.id === 1 ? { interval: interval[0] * 60 + interval[1] } : {}),
            stages: states.map((state, idx) => ({
              name: `${idx}`,
              actions: automation?.blueprint?.id === 1 ? [{
                action_type: "Maintain Fan Unit Count and Temperature Setpoint",
                parameters: state?.actions?.map(param => ({
                  name: param.name,
                  value: param.value,
                }))
              }] :
                [{
                  action_type: state?.actions?.name,
                  parameters: state?.actions?.parameters?.map(parameter => ({
                    device_ids: parameter.device_ids,
                    mode: parameter.mode,
                    setpoint: parameter.value,
                  }))
                }],
              conditions: _.flatten(state.conditions?.map((condition, conditionIdx) => {
                if (conditionIdx > 0) return [{
                  logical_operator: condition.logical_operator,
                }, {
                  value: condition.value,
                  parameter: condition.parameter,
                  operator: condition.operator,
                }]
                else return ({
                  value: condition.value,
                  parameter: condition.parameter,
                  operator: condition.operator,
                })
              })),
              position: idx + 1,
            })),
            constraints: {
              device_constraints: constraints?.map(constraint => ({
                device_ids: constraint.device_ids,
                attributes: constraint.attributes,
              }))
            },
          }
        });
        refetch()
        message.open({
          type: 'success',
          content: SUCCESS_MESSAGE()
        });
        // Exit edit mode after successful save
        setIsEdit(false)
      } catch (e) {
        message.open({
          type: 'error',
          content: (e as Error).message
        });
      }
    }
  }

  const handleBack = () => {
    navigate('/settings/automations');
  };

  const handleStatusChange = async (status) => {
    try {
      await editAutomation(automationId, {
        enable_status: status,
      });
      refetch()
      message.open({
        type: 'success',
        content: SUCCESS_MESSAGE()
      });
    } catch (e) {
      message.open({
        type: 'error',
        content: (e as Error).message
      });
    }
  }

  useEffect(() => {
    if (!automationData) return
    setAutomation({
      ...automationData,
      parameterUsed: automationData.blueprint_parameters.map(param => param.name),
      usedInProfile: automationData.used_by_profiles.map(profile => profile.profile_name),
    })
    setAutomationName(automationData.name)
    // Convert interval from seconds to minutes
    if (automationData.config?.interval !== undefined) {
      setInterval([Math.floor(automationData.config.interval / 60), 0])
    }
    setStates(automationData.config?.stages?.map((stage, idx) => ({
      id: `state-${idx + 1}`,
      conditions: _.compact(stage?.conditions?.map((condition, idx) => {
        if (condition.logical_operator) return null
        // Find the parameter details to get the unit and constraints
        const parameterDetails = automationData.blueprint_parameters.find(p => p.name === condition.parameter);
        return ({
          value: condition.value,
          parameter: condition.parameter,
          operator: condition.operator,
          suffix: parameterDetails?.unit,
          min: parameterDetails?.constraints?.min_value,
          max: parameterDetails?.constraints?.max_value,
          digit: parameterDetails?.constraints?.decimal_places_allowed,
          ...idx > 0 ? {
            logical_operator: stage?.conditions[idx - 1].logical_operator,
          } : {}
        })
      })),
      actions: automationData?.blueprint?.id === 1 ? stage?.actions?.[0]?.parameters?.map(param => ({
        name: param.name,
        value: param.value,
      })) : {
        name: stage?.actions[0].action_type,
        parameters: stage?.actions[0].parameters.map(parameter => ({
          device_ids: parameter.device_ids,
          mode: parameter.mode,
          value: parameter.setpoint,
        }))
      },
    })))
    setConstraints(automationData.config.constraints?.device_constraints?.map(constraint => ({
      device_ids: constraint.device_ids,
      attributes: constraint.attributes,
    })))
  }, [automationData])

  useEffect(() => {
    if (!systemData) return
    setAvailableDevices(systemData.devices?.map(device => ({
      id: device.device_id,
      name: device.name,
    })))
  }, [systemData])

  useEffect(() => {
    if (!blueprintsData) return
    const bluePrintData = blueprintsData.find(blueprint => blueprint.id === automation?.blueprint?.id)
    if (!bluePrintData) return
    setParameters(bluePrintData.parameters)
  }, [blueprintsData, automation])

  return (
    <div className="flex flex-col h-full bg-bg">
      {/* Header */}
      <div className="px-4 pt-4">
        <ViewAutomationHeader isEdit={isEdit} name={automation?.name} handleBack={handleBack} navigate={navigate} onEdit={onEdit} />
      </div>

      {/* Content */}
      <div className="flex flex-1 min-h-0">
        {/* Left Sidebar */}
        <AutomationDetailSidebar isEnabled={automation?.enable_status} lastTriggered={automation?.lastTriggered} usedInProfile={automation?.usedInProfile}
          parameterUsed={automation?.parameterUsed} blueprint={blueprints[automation?.blueprint?.id - 1]} handleStatusChange={handleStatusChange} />

        {/* Main Content */}
        <AutomationDetailMain blueprint={automation?.blueprint} activeTab={activeTab} setActiveTab={setActiveTab} disabled={disabled}
          states={states}
          setStates={setStates}
          interval={interval}
          setInterval={setInterval}
          automationName={automationName}
          setAutomationName={setAutomationName}
          selectedBlueprint={automation?.blueprint?.id === 1 ? 'Flowsave' : '2'}
          constraints={constraints}
          setConstraints={setConstraints}
          availableDevices={availableDevices}
          parameters={parameters}
        />
      </div>
    </div>
  );
}